import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { IonIcon, IonItemGroup, useIonViewDidLeave } from "@ionic/react";

import LoginHeader from "../../organisms/LoginHeader";
import CategoryForm from "../../molecules/CategoryForm";
import {
  ICategory,
  IClass,
  IStudentGearBox,
  ITeacher,
  IRealizationImage,
  IGearTheme,
  IRealizationCreateParams,
  IRealizationFeedback,
  IStudentRealizationFeedback,
  EvaluationType,
  IPulseSurveyAnswer,
} from "../../state";
import { isImageFile } from "../../libs/Util";
import dailyStockIcon from "../../assets/icons/daily_stock.svg";
import feedbackIcon from "../../assets/icons/good_job.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import RealizationTextArea, {
  CONTENT_MAX_LENGTH,
} from "../../atoms/RealizationTextArea";
import RealizationButton from "../../atoms/RealizationButton";
import RealizationThumbnailImage from "../../atoms/RealizationThumbnailImage";
import WordCounter from "../../atoms/WordCounter";
import CommentModal from "../../atoms/CommentModal";
import ImageDetailModal from "../../molecules/ImageDetailModal";
import ModalConfirm from "../../molecules/ModalConfirm";
import ThemeHeader from "../../molecules/ThemeHeader";
import Toast, { TToastType } from "../../molecules/Toast";
import BoxTemplate from "../../molecules/BoxTemplate";
import BoxToggle from "../../molecules/BoxToggle";
import EvaluationToggle from "../../molecules/EvaluationToggle";
import TargetGroup, { ETargetSwitch } from "../../organisms/TargetGroup";
import PulseSurveyAnswerModal from "../../organisms/PulseSurveyModal/Answer";
import PulseSurveyCompleteModal from "../../organisms/PulseSurveyModal/Complete";
import { useBoxToggle } from "../../hooks/useBoxToggle";
import { useTargetGroupUpdate } from "../../hooks/useTargetGroupUpdate";
import {
  Context,
  CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE,
  CREATE_REALIZATION_SUCCESS_MESSAGE,
  INPUT_REALIZATION_CONTENT_FAILURE_MESSAGE,
  SELECT_REALIZATION_BOX_FAILURE_MESSAGE,
  SELECT_REALIZATION_CLASS_FAILURE_MESSAGE,
  SELECT_REALIZATION_TEACHER_FAILURE_MESSAGE,
} from "../../store/student";
import { MAX_IMAGE_SIZE_MB } from "../../constants/upload";

import styles from "./RealizationForm.module.scss";

export type RealizationFormProps = {
  pathname: string;
  success_message: string;
  categories: ICategory[];
  teachers: ITeacher[];
  classes: IClass[];
  gearBoxes: IStudentGearBox[];
  calling: boolean;
  unreadNotiCount: number;
  stock_c_to_c_enabled: boolean;
  realization_feedback_creatable: boolean;
  student_realization_feedbacks: IStudentRealizationFeedback;
  gearTheme: IGearTheme | null;
  createRealization(
    realization: IRealizationCreateParams,
    submit_teacher_ids: number[],
    share_class_ids: number[],
    gearbox_id: number | null,
    image?: IRealizationImage,
    geartheme_id?: number,
  ): void;
  createCategory(category: { name: string; color: string }): void;
  submitRealizationFeedbackEvaluation: (
    id: number,
    type: EvaluationType,
  ) => void;
  createRealizationFeedbacks: () => void;
  updateReadAtToRealizationFeedback: (realization_feedback_id: number) => void;
  createRealizationConversation?: (realization_id: number) => void;
  submitPulseSurveyAnswers: (answers: IPulseSurveyAnswer) => void;
  declinePulseSurveyAnswers: () => void;
};

const INITIAL_STATE = {
  content: "",
  color: "",
  category_ids: [],
  selected_teacher_ids: [],
  selected_class_ids: [],
  selected_evaluation_type: "neutral" as EvaluationType,
  image: undefined,
  geartheme_id: undefined,
  toast: null,
  showCategoryForm: false,
  showSubmitForm: false,
  showShareForm: false,
  showImageInvalid: false,
  showImageExceedsSize: false,
  showImageDetail: false,
  showRealizationFeedback: false,
  disabledFeedbackBtn: false,
  selected_box_id: null,
  targetSwitch: ETargetSwitch.ONLY_ME,
  realization_feedback: undefined,
  isBoxToggleOn: false,
  showBoxDropdown: false,
};

const RealizationForm = (props: RealizationFormProps) => {
  const history = useHistory();
  const realizationImgInputRef = React.useRef<HTMLInputElement>(null);
  const { dispatch, contextState } = React.useContext(Context);
  const [isShowModalConfirm, setIsShowModalConfirm] = React.useState(false);
  const [hasTriggeredConversation, setHasTriggeredConversation] =
    React.useState(false);
  const [pendingConversationCreation, setPendingConversationCreation] =
    React.useState(false);
  const [nextLocation, setNextLocation] = React.useState("/realization");
  const [state, setState] = React.useState<{
    content: string;
    color: string;
    category_ids: number[];
    selected_teacher_ids: number[];
    selected_class_ids: number[];
    selected_box_id: number | null;
    selected_evaluation_type: EvaluationType;
    image?: IRealizationImage;
    toast: {
      type: TToastType;
      message: string;
    } | null;
    showCategoryForm: boolean;
    showSubmitForm: boolean;
    showShareForm: boolean;
    showImageInvalid: boolean;
    showImageExceedsSize: boolean;
    showImageDetail: boolean;
    showRealizationFeedback: boolean;
    targetSwitch: ETargetSwitch;
    geartheme_id?: number;
    disabledFeedbackBtn: boolean;
    realization_feedback: IRealizationFeedback | undefined;
    isBoxToggleOn: boolean;
    showBoxDropdown: boolean;
  }>(INITIAL_STATE);

  const {
    boxDropdownRef,
    toggleButtonRef,
    selectedNameRef,
    handleBoxToggle,
    handleBoxSelect,
    handleToggleDropdown,
  } = useBoxToggle(setState, state.showBoxDropdown);

  const { handleTargetGroupUpdate } = useTargetGroupUpdate(state, setState);

  const update = React.useCallback((args: { [key: string]: any }) => {
    setState(prevState => ({ ...prevState, ...args }));
  }, []);

  const [isShowPulseSurveyButton, setIsShowPulseSurveyButton] =
    React.useState<boolean>(false);
  const [isOpenPulseSurveyAnswerModal, setIsOpenPulseSurveyAnswerModal] =
    React.useState<boolean>(false);
  const [isOpenPulseSurveyCompleteModal, setIsOpenPulseSurveyCompleteModal] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    const blockOfHistoryPop = history.block((location, _action) => {
      const regex = /\/realizations\/category\/\d+/;
      const isCurrentPath = nextLocation === "/realization";
      const isCreateStockPath = location.pathname.match(regex);

      // 入力中のストックが存在し、ストック作成以外の動線に遷移しようとした場合にモーダルを表示
      if (state.content.length > 0 && isCurrentPath && !isCreateStockPath) {
        setNextLocation(location.pathname);
        setIsShowModalConfirm(true);
        return false;
      } else {
        setNextLocation("/realization");
      }
    });

    return () => blockOfHistoryPop();
  }, [history, state.content, nextLocation]);

  const [errorMessage, setErrorMessage] = React.useState("");

  useIonViewDidLeave(() => {
    update(INITIAL_STATE);
  });

  React.useEffect(() => {
    const realization_feedback =
      props.student_realization_feedbacks?.realization_feedbacks[0];
    if (!realization_feedback) return;
    setState(s => ({
      ...s,
      realization_feedback,
    }));
    if (realization_feedback.read_at) return;
    props.updateReadAtToRealizationFeedback(realization_feedback.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.student_realization_feedbacks]);

  React.useEffect(() => {
    setState(s => ({
      ...s,
      selected_evaluation_type:
        state.realization_feedback?.evaluation_status ?? "neutral",
    }));
  }, [state.realization_feedback]);

  React.useEffect(() => {
    setState(s => ({
      ...s,
      selected_evaluation_type:
        contextState.realization_feedback_evaluation_status,
    }));
  }, [contextState.realization_feedback_evaluation_status]);

  React.useEffect(() => {
    if (contextState.error) {
      setErrorMessage(contextState.error);
    }
  }, [dispatch, contextState.error]);

  const removeImg = useCallback((e?) => {
    if (e) {
      e.stopPropagation();
    }
    realizationImgInputRef.current &&
      (realizationImgInputRef.current.value = "");
    setState(s => ({ ...s, image: undefined }));
  }, []);

  const uploadImg = useCallback(
    e => {
      e.persist();
      const file = e.target.files[0];
      if (!file) {
        return;
      }
      if (!isImageFile(file)) {
        removeImg();
        setState(s => ({ ...s, showImageInvalid: true }));
        return;
      }
      const fileSizeByMB = file.size / 1024 / 1024;
      if (fileSizeByMB > MAX_IMAGE_SIZE_MB) {
        removeImg();
        setState(s => ({ ...s, showImageExceedsSize: true }));
        return;
      }
      setState(s => ({
        ...s,
        image: {
          name: file.name,
          url: URL.createObjectURL(file),
          blob: file,
        },
      }));
    },
    [removeImg],
  );

  const selectedBox = state.selected_box_id
    ? props.gearBoxes.find(box => box.id === state.selected_box_id)
    : undefined;

  const onSubmit = (kind: "roots" | "will") => {
    if (!state.content) {
      update({
        toast: {
          type: "danger",
          message: INPUT_REALIZATION_CONTENT_FAILURE_MESSAGE,
        },
      });
      return;
    }
    if (state.isBoxToggleOn && !state.selected_box_id) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_BOX_FAILURE_MESSAGE,
        },
      });
      return;
    }
    if (
      state.targetSwitch === ETargetSwitch.GROUP &&
      !state.selected_class_ids.length
    ) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_CLASS_FAILURE_MESSAGE,
        },
      });
      return;
    }
    if (
      state.targetSwitch === ETargetSwitch.TEACHER &&
      !state.selected_teacher_ids.length
    ) {
      update({
        toast: {
          type: "danger",
          message: SELECT_REALIZATION_TEACHER_FAILURE_MESSAGE,
        },
      });
      return;
    }
    props.createRealization(
      {
        category_ids: state.category_ids,
        content: state.content,
        submitted: state.selected_teacher_ids.length > 0,
        shared: state.selected_class_ids.length > 0 || !!props.gearTheme,
        kind,
      },
      selectedBox
        ? selectedBox.assigned_teachers.map(teacher => teacher.id)
        : state.selected_teacher_ids,
      selectedBox
        ? selectedBox.klasses.map(cls => cls.id)
        : state.selected_class_ids,
      state.selected_box_id,
      state.image,
      props.gearTheme?.id,
    );
  };

  React.useEffect(() => {
    if (
      props.success_message === CREATE_REALIZATION_SUCCESS_MESSAGE ||
      props.success_message === CREATE_REALIZATION_BY_CONVERSION_SUCCESS_MESSAGE
    ) {
      if (!hasTriggeredConversation && !pendingConversationCreation) {
        setPendingConversationCreation(true);
      }
    }
    if (
      !props.calling &&
      pendingConversationCreation &&
      !hasTriggeredConversation &&
      contextState.realization &&
      props.createRealizationConversation
    ) {
      const realization = contextState.realization;

      if (realization.categories && realization.categories.length > 0) {
        props.createRealizationConversation(realization.id);
        setHasTriggeredConversation(true);
        setPendingConversationCreation(false);
      } else {
        setPendingConversationCreation(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.success_message,
    props.calling,
    contextState.realization?.id,
    hasTriggeredConversation,
    pendingConversationCreation,
  ]);

  React.useEffect(() => {
    if (!props.success_message) {
      setHasTriggeredConversation(false);
      setPendingConversationCreation(false);
    }
  }, [props.success_message]);

  React.useEffect(() => {
    setIsShowPulseSurveyButton(
      contextState.student.available_pulse_survey_answers,
    );
  }, [contextState.student.available_pulse_survey_answers]);

  return (
    <IonItemGroup className={styles.wrapper}>
      <Toast
        type={state.toast?.type}
        showToast={!!state.toast}
        onClose={() => update({ toast: null })}
        message={state.toast?.message ?? ""}
      />
      <ModalConfirm
        isOpen={state.showImageInvalid}
        message={`無効なファイル形式です。\njpg/jpeg/png のみアップロード可能です。`}
        buttons={[
          {
            title: "キャンセル",
            type: "None",
            action: () => setState(s => ({ ...s, showImageInvalid: false })),
          },
          {
            title: "OK",
            type: "Success",
            action: () => setState(s => ({ ...s, showImageInvalid: false })),
          },
        ]}
      />
      <ModalConfirm
        isOpen={state.showImageExceedsSize}
        message={`アップロードに失敗しました。\nファイルサイズを${MAX_IMAGE_SIZE_MB}MB以下にしてください。`}
        buttons={[
          {
            title: "OK",
            type: "None",
            action: () =>
              setState(s => ({ ...s, showImageExceedsSize: false })),
          },
        ]}
      />
      <ModalConfirm
        isOpen={isShowModalConfirm}
        message={`ページを遷移すると、現在入力中の文章が消えてしまいます。移動してもよろしいですか？`}
        buttons={[
          {
            title: "キャンセル",
            type: "None",
            action: () => {
              // モーダル表示のタイミングでnextLocationが更新されてしまうため、デフォルトに戻す
              setNextLocation("/realization");
              setIsShowModalConfirm(false);
            },
          },
          {
            title: "OK",
            type: "Success",
            action: () => {
              setIsShowModalConfirm(false);
              history.push(nextLocation);
              // history.pushでDOMが更新されないケースがあるため暫定対処
              window.location.reload();
            },
          },
        ]}
      />
      {state.image && (
        <ImageDetailModal
          show={state.showImageDetail}
          imageUrl={state.image.url}
          imageName={state.image.name}
          onClose={() => setState(s => ({ ...s, showImageDetail: false }))}
        />
      )}
      <CommentModal
        isOpen={state.showRealizationFeedback}
        onClickBackdrop={() =>
          setState(s => ({ ...s, showRealizationFeedback: false }))
        }
      >
        <div className={styles.feedbackModal}>
          <span
            className={styles.close}
            onClick={() =>
              setState(s => ({ ...s, showRealizationFeedback: false }))
            }
          />
          {props.calling && !state.realization_feedback ? (
            <p className={styles.callingMessage}>フィードバック作成中</p>
          ) : errorMessage.length > 0 ? (
            errorMessage
          ) : (
            <div className={styles.inner}>
              <p className={styles.title}>
                GEARから
                {props.student_realization_feedbacks.last_name}{" "}
                {props.student_realization_feedbacks.first_name}
                さんへのフィードバック
              </p>
              <p className={styles.content}>
                {state.realization_feedback?.content}
              </p>
              <div className={styles.footer}>
                <p className={styles.date}>
                  {state.realization_feedback?.created_at}
                </p>
                <EvaluationToggle
                  color={"#20C997"}
                  type={state.selected_evaluation_type}
                  calling={props.calling}
                  onEvaluate={type => {
                    setState(s => ({
                      ...s,
                      selected_evaluation_type: type,
                    }));
                    props.submitRealizationFeedbackEvaluation(
                      state.realization_feedback?.id ?? 0,
                      type,
                    );
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </CommentModal>
      <PulseSurveyAnswerModal
        isOpen={isOpenPulseSurveyAnswerModal}
        onClose={() => {
          props.declinePulseSurveyAnswers();
          setIsOpenPulseSurveyAnswerModal(false);
        }}
        onSubmitAnswers={answers => {
          props.submitPulseSurveyAnswers(answers);
          setIsOpenPulseSurveyAnswerModal(false);
          setIsOpenPulseSurveyCompleteModal(true);
        }}
      />
      <PulseSurveyCompleteModal
        isOpen={isOpenPulseSurveyCompleteModal}
        closeModal={() => setIsOpenPulseSurveyCompleteModal(false)}
      />
      <LoginHeader
        title="ストック"
        unreadNotiCount={props.unreadNotiCount}
        updateTarget={state.content}
      />
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          {!props.gearTheme && (
            <BoxToggle
              isBoxToggleOn={state.isBoxToggleOn}
              showBoxDropdown={state.showBoxDropdown}
              selectedBox={selectedBox}
              gearBoxes={props.gearBoxes}
              selected_box_id={state.selected_box_id}
              toggleButtonRef={toggleButtonRef}
              selectedNameRef={selectedNameRef}
              boxDropdownRef={boxDropdownRef}
              onToggle={handleBoxToggle}
              onToggleDropdown={handleToggleDropdown}
              onSelectBox={handleBoxSelect}
            />
          )}
          {props.gearTheme && (
            <ThemeHeader geartheme={props.gearTheme} hideInputButton={true} />
          )}
          {selectedBox?.template && (
            <BoxTemplate
              title={selectedBox.title}
              boxTemplate={selectedBox.template}
            />
          )}
          <div className={styles.inputArea}>
            <div className={styles.textAreaWrapper}>
              <RealizationTextArea
                content={state.content}
                onChange={value => setState({ ...state, content: value })}
              />
            </div>
            <div className={styles.imgInputWrapper}>
              {state.image && (
                <div className={styles.realizationImg}>
                  <RealizationThumbnailImage
                    alt="Upload Image"
                    src={state.image.url}
                    onClick={e => {
                      e.stopPropagation();
                      setState(s => ({ ...s, showImageDetail: true }));
                    }}
                    isEdit={true}
                  />
                  <button onClick={removeImg}>✕</button>
                </div>
              )}
              <div className={styles.rightControlArea}>
                <WordCounter
                  maxLength={CONTENT_MAX_LENGTH}
                  targetLength={state.content.length}
                />
                {!state.image && (
                  <>
                    <input
                      ref={realizationImgInputRef}
                      accept=".png,.jpg,.jpeg"
                      type="file"
                      onChange={uploadImg}
                      className={styles.hiddenInput}
                    />
                    <ImageIcon
                      onClick={() => realizationImgInputRef.current?.click()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          {!props.gearTheme && (
            <TargetGroup
              teachers={props.teachers}
              classes={props.classes}
              stock_c_to_c_enabled={props.stock_c_to_c_enabled}
              showSubmitForm={state.showSubmitForm}
              showShareForm={state.showShareForm}
              targetSwitch={state.isBoxToggleOn ? null : state.targetSwitch}
              selected_teacher_ids={state.selected_teacher_ids}
              selected_class_ids={state.selected_class_ids}
              update={handleTargetGroupUpdate}
              excludeRef={boxDropdownRef}
              disabled={state.isBoxToggleOn && !!state.selected_box_id}
            />
          )}
          <CategoryForm
            pathname={props.pathname}
            success_message={props.success_message}
            calling={props.calling}
            isOpen={state.showCategoryForm}
            categories={props.categories}
            selectedCategoryIDs={state.category_ids}
            update={update}
            createCategory={props.createCategory}
          />
          <div className={styles.buttonArea}>
            {isShowPulseSurveyButton && (
              <div className={styles.pulseSurveyArea}>
                <button
                  className={styles.pulseSurveyButton}
                  type="button"
                  onClick={() => setIsOpenPulseSurveyAnswerModal(true)}
                >
                  <IonIcon
                    icon={dailyStockIcon}
                    className={styles.pulseSurveyIcon}
                  />
                  デイリーストック
                </button>
              </div>
            )}
            {props.realization_feedback_creatable && (
              <div className={styles.feedbackArea}>
                <button
                  className={styles.feedbackButton}
                  onClick={() => {
                    setState(s => ({
                      ...s,
                      disabledFeedbackBtn: true,
                    }));
                    props.createRealizationFeedbacks();
                    setState(s => ({ ...s, showRealizationFeedback: true }));
                  }}
                  disabled={state.disabledFeedbackBtn}
                >
                  <IonIcon
                    icon={feedbackIcon}
                    className={styles.feedbackIcon}
                  />
                  GEARからのフィードバック
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.wrapperButton}>
          <RealizationButton
            kind={"roots"}
            isDisabled={props.calling}
            onClick={() => onSubmit("roots")}
          />
          <RealizationButton
            kind={"will"}
            isDisabled={props.calling}
            onClick={() => onSubmit("will")}
          />
        </div>
      </div>
    </IonItemGroup>
  );
};

export default RealizationForm;
